import {
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { HlmFieldImports } from '@mb/ui/field';
import { HlmInputImports } from '@mb/ui/input';
import type { PersonSearchDto } from '../../shared';

/** Searchable, scrollable multi-select list of persons. Filters locally. */
@Component({
  selector: 'contacts-person-search-list',
  standalone: true,
  imports: [HlmFieldImports, HlmInputImports],
  template: `
    <div hlmFieldGroup class="gap-2">
      <div hlmField>
        <input
          hlmInput
          type="search"
          [id]="inputId()"
          [placeholder]="placeholder()"
          [value]="search()"
          (input)="onSearchInput($event)"
        />
      </div>

      <div
        class="max-h-48 overflow-y-auto rounded-md border border-border"
        role="listbox"
        aria-multiselectable="true"
      >
        @if (filteredPersons().length === 0) {
          <p class="px-3 py-2 text-sm text-muted-foreground">
            {{ emptyLabel() }}
          </p>
        } @else {
          <ul class="divide-y divide-border">
            @for (person of filteredPersons(); track person.id) {
              <li>
                <label
                  class="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-accent/50"
                  [attr.aria-selected]="isSelected(person.id)"
                >
                  <input
                    type="checkbox"
                    class="size-4 shrink-0 rounded border border-input accent-primary"
                    [checked]="isSelected(person.id)"
                    (change)="toggle(person.id)"
                  />
                  <span class="min-w-0 flex-1 truncate">
                    <span class="font-medium text-foreground">{{
                      personLabel(person)
                    }}</span>
                    @if (person.email) {
                      <span class="block truncate text-muted-foreground">{{
                        person.email
                      }}</span>
                    }
                  </span>
                </label>
              </li>
            }
          </ul>
        }
      </div>
    </div>
  `,
})
export class PersonSearchListComponent {
  private static nextId = 0;

  readonly persons = input.required<readonly PersonSearchDto[]>();
  readonly selectedIds = input<readonly string[]>([]);
  readonly placeholder = input('Search…');
  readonly emptyLabel = input('No persons found.');
  readonly inputId = input(
    `contacts-person-search-${PersonSearchListComponent.nextId++}`,
  );

  readonly selectedIdsChange = output<readonly string[]>();

  readonly search = signal('');

  readonly filteredPersons = computed((): readonly PersonSearchDto[] => {
    const query = this.search().trim().toLowerCase();
    const selected = new Set(this.selectedIds());
    const persons = this.persons();

    const matches = !query
      ? [...persons]
      : persons.filter((person) => {
          const name = `${person.firstName} ${person.lastName}`
            .trim()
            .toLowerCase();
          const email = person.email.toLowerCase();
          return name.includes(query) || email.includes(query);
        });

    // Keep already-selected people visible at the top of the list.
    return matches.sort((a, b) => {
      const aSelected = selected.has(a.id) ? 0 : 1;
      const bSelected = selected.has(b.id) ? 0 : 1;
      return aSelected - bSelected;
    });
  });

  personLabel(person: PersonSearchDto): string {
    const name = `${person.firstName} ${person.lastName}`.trim();
    return name || person.email || person.id;
  }

  isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.search.set(value);
  }

  toggle(id: string): void {
    const current = this.selectedIds();
    const next = current.includes(id)
      ? current.filter((selectedId) => selectedId !== id)
      : [...current, id];
    this.selectedIdsChange.emit(next);
  }
}
